import React, { useEffect, useState } from 'react';
import axios from 'axios';

function Toggle({ id, label, checked, onChange, disabled, children }) {
    return (
        <div className="mb-2">
            <div className="form-check form-switch">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id={id}
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.checked)}
                />
                <label className="form-check-label fw-semibold small" htmlFor={id}>
                    {label}
                </label>
            </div>
            {checked && children && <div className="mt-2 ms-4">{children}</div>}
        </div>
    );
}

function MultiCheckList({ items, selectedIds, onToggle, labelKey = 'name', emptyText = 'None available' }) {
    if (!items.length) return <p className="text-muted small mb-0">{emptyText}</p>;
    return (
        <div className="d-flex flex-column gap-1" style={{ maxHeight: 160, overflowY: 'auto' }}>
            {items.map((item) => {
                const selected = selectedIds.includes(item.id);
                return (
                    <div key={item.id} className="form-check mb-0">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id={`mcl-${item.id}`}
                            checked={selected}
                            onChange={() => onToggle(item.id)}
                        />
                        <label className="form-check-label small" htmlFor={`mcl-${item.id}`}>
                            {item[labelKey]}
                        </label>
                    </div>
                );
            })}
        </div>
    );
}

export default function CampaignAudienceSelector({ audience, onChange, leagueId, disabled }) {
    const [seasons, setSeasons] = useState([]);
    const [teams, setTeams] = useState([]);
    const [emailLists, setEmailLists] = useState([]);
    const [playerSearch, setPlayerSearch] = useState('');
    const [playerResults, setPlayerResults] = useState([]);

    useEffect(() => {
        if (!leagueId) return;
        axios.get(`/api/leagues/seasons/?league=${leagueId}`, { withCredentials: true })
            .then((r) => setSeasons(r.data.results || r.data))
            .catch(() => {});
        axios.get(`/api/teams/?league=${leagueId}`, { withCredentials: true })
            .then((r) => setTeams(r.data.results || r.data))
            .catch(() => {});
        axios.get(`/api/campaigns/email-lists/?league=${leagueId}`, { withCredentials: true })
            .then((r) => setEmailLists(r.data.results || r.data))
            .catch(() => {});
    }, [leagueId]);

    useEffect(() => {
        if (!playerSearch.trim() || !leagueId) { setPlayerResults([]); return; }
        const t = setTimeout(() => {
            axios.get(`/api/player/?league=${leagueId}&search=${encodeURIComponent(playerSearch)}`, { withCredentials: true })
                .then((r) => setPlayerResults(r.data.results || r.data))
                .catch(() => {});
        }, 300);
        return () => clearTimeout(t);
    }, [playerSearch, leagueId]);

    const set = (key, val) => onChange({ ...audience, [key]: val });

    const toggleId = (key, id) => {
        const ids = audience[key] || [];
        set(key, ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
    };

    const togglePlayer = (player) => {
        const ids = audience.selected_player_ids || [];
        const names = audience.selected_player_names || [];
        if (ids.includes(player.id)) {
            onChange({
                ...audience,
                selected_player_ids: ids.filter((id) => id !== player.id),
                selected_player_names: names.filter((_, i) => ids[i] !== player.id),
            });
        } else {
            const name = `${player.user?.first_name || ''} ${player.user?.last_name || ''}`.trim() || player.user?.email || String(player.id);
            onChange({
                ...audience,
                selected_player_ids: [...ids, player.id],
                selected_player_names: [...names, name],
            });
        }
    };

    return (
        <div>
            <div className="small fw-semibold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.04em' }}>
                Audience segments
            </div>

            <Toggle id="aud-league" label="All current league players" checked={audience.include_league_players} onChange={(v) => set('include_league_players', v)} disabled={disabled} />

            <Toggle id="aud-season" label="Seasons" checked={audience.include_season_players} onChange={(v) => set('include_season_players', v)} disabled={disabled}>
                <MultiCheckList
                    items={seasons}
                    selectedIds={audience.season_ids || []}
                    onToggle={(id) => toggleId('season_ids', id)}
                    emptyText="No seasons found for this league."
                />
            </Toggle>

            <Toggle id="aud-team" label="Teams" checked={audience.include_team_players} onChange={(v) => set('include_team_players', v)} disabled={disabled}>
                <MultiCheckList
                    items={teams}
                    selectedIds={audience.team_ids || []}
                    onToggle={(id) => toggleId('team_ids', id)}
                    emptyText="No teams found for this league."
                />
            </Toggle>

            <Toggle id="aud-players" label="Selected players" checked={audience.include_selected_players} onChange={(v) => set('include_selected_players', v)} disabled={disabled}>
                <input
                    className="form-control form-control-sm mb-2"
                    placeholder="Search by name or email…"
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    disabled={disabled}
                />
                {playerResults.length > 0 && (
                    <ul className="list-group list-group-flush mb-2" style={{ maxHeight: 140, overflowY: 'auto' }}>
                        {playerResults.map((p) => {
                            const selected = (audience.selected_player_ids || []).includes(p.id);
                            const name = `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim() || p.user?.email || p.id;
                            return (
                                <li
                                    key={p.id}
                                    className={`list-group-item list-group-item-action small py-1 ${selected ? 'list-group-item-primary' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => togglePlayer(p)}
                                >
                                    {name} <span className="text-muted">— {p.user?.email}</span>
                                </li>
                            );
                        })}
                    </ul>
                )}
                {(audience.selected_player_ids || []).length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-1">
                        {(audience.selected_player_names || audience.selected_player_ids).map((name, i) => (
                            <span
                                key={audience.selected_player_ids[i]}
                                className="badge bg-primary d-flex align-items-center gap-1"
                                style={{ cursor: 'pointer' }}
                                onClick={() => togglePlayer({ id: audience.selected_player_ids[i], user: {} })}
                            >
                                {name} ×
                            </span>
                        ))}
                    </div>
                )}
            </Toggle>

            <Toggle id="aud-subscribers" label="Subscribers" checked={audience.include_subscribers} onChange={(v) => set('include_subscribers', v)} disabled={disabled}>
                {emailLists.length > 0 ? (
                    <MultiCheckList
                        items={emailLists}
                        selectedIds={audience.selected_email_list_ids || []}
                        onToggle={(id) => toggleId('selected_email_list_ids', id)}
                    />
                ) : (
                    <p className="text-muted small mb-0">No email lists found. All active subscribers will be included.</p>
                )}
            </Toggle>

            <Toggle
                id="aud-previous"
                label="Previous players"
                checked={audience.include_previous_players}
                onChange={(v) => set('include_previous_players', v)}
                disabled={disabled}
            />
        </div>
    );
}
